// Speiler api/src/types.ts. Holdes manuelt i synk — MVP har ingen delt pakke.
export type Lang = "no" | "en";

/**
 * Språket grensesnittet står på.
 *
 * Holdt adskilt fra `Lang` med vilje, og skillet er ikke kosmetisk.
 *
 * `Lang` er språket *innholdet* finnes på. På serversiden er `Localized =
 * Record<Lang, string>`, så hver verdi der — 118 stedsbeskrivelser, hver
 * attraksjonsnote, hver vinteradvarsel, hver tidsadvarsel på en fottur — må
 * finnes på alle språkene i `Lang`. Det er tusenvis av linjer redaksjonell
 * prosa, skrevet for hånd, og maskinoversatt ville den blitt merkbart dårligere.
 *
 * `UiLang` er språket *appen* snakker: knapper, overskrifter, forklaringer,
 * advarsler appen selv formulerer. Det er 277 nøkler, og de kan oversettes godt.
 *
 * En tysk reisende får derfor tysk grensesnitt og engelske stedsbeskrivelser.
 * Det er ikke idealet, men det er ærlig — og langt bedre enn å ikke tilby tysk,
 * eller å tilby en tysk app der halve teksten er maskinoversatt norsk.
 */
export type UiLang = Lang | "de" | "fr" | "es";
export type VibeTag = "fjell" | "fjord" | "by";
export type Vibe = VibeTag | "miks";
/**
 * Hvordan et sted faktisk nås. Et faktum om stedet. Speiler api/src/types.ts.
 *
 * Løsnet fra `TravelMode` med vilje: «kollektivt» er ikke et faktum om en vei,
 * det er et filter den reisende velger. Var det medlem her, kunne
 * `Stop.modes` holdt det — og et oppslag på det ville svart nei for hvert stopp.
 */
export type Mode = "tog" | "bil" | "buss" | "fottur";

/**
 * Reisemåten den reisende velger. Speiler api/src/types.ts.
 *
 * `kollektiv` er standarden: å velge buss skal ikke forby toget. Tokenene er
 * **bare-legg-til** — `reise=` skrives ubetinget i delte lenker, så `buss`
 * betyr fortsatt buss i hver lenke som alt er sendt.
 */
export type TravelMode = "kollektiv" | "tog" | "buss" | "bil";
export type Pace = "avslappet" | "aktiv";

export interface Weather {
  symbol: string;
  tempMin: number;
  tempMax: number;
  precipitationMm: number;
  /** Skydekke i prosent, snitt over dagtimene. */
  cloudPercent?: number;
  /** Tåke i prosent, verste time i dagtimene. Bare fra MET — se `source`. */
  fogPercent?: number;
  /**
   * Hvilken modell tallene kommer fra. Speiler api/src/types.ts.
   *
   * `met` rekker rundt ni døgn på en kilometer, `ecmwf` rekker seksten på 28.
   * Mangler på en lagret plan fra før feltet fantes, og leses da som `met`.
   */
  source?: "met" | "ecmwf";
}

export interface ViewScore {
  score: number;
  reason: string;
  suggestion?: string;
  /**
   * Hva tallet er regnet av: et varsel, et langtidsvarsel, en klimanormal,
   * eller ingenting.
   *
   * Grensesnittet må skille dem, for det er fire ulike påstander: 58 fra et
   * varsel er noe å planlegge etter, 58 fra et langtidsvarsel er noe å følge
   * med på, 58 fra en normal er noe å ta høyde for, og 60 uten grunnlag er
   * ikke et anslag i det hele tatt. Speiler api/src/types.ts. Feltet kan
   * mangle på en lagret plan fra før det fantes.
   */
  basis?: "forecast" | "extended" | "normals" | "none";
}

/** Hva klimaet sier om ett stopp i én måned. Speiler api/src/types.ts. */
export interface StopOutlook {
  month: number;
  tMin: number;
  tMax: number;
  /** Andelen døgn med nedbør, 0–1. */
  wetShare: number;
  /** Andelen av de våte døgnene der nedbøren kommer som snø, 0–1. */
  snowShare: number;
  cloudPct: number;
  score: number;
  reason: string;
}

/**
 * Langtidsvarselet for døgn 10–16. Speiler api/src/types.ts.
 *
 * Et *varsel* og ikke en normal, og det er hele grunnen til at det er en egen
 * type: det varierer fra dag til dag, så dagsgrensene kan legges etter det.
 * Grovere i rommet og svakere i tiden enn MET sitt, og hvert av de forbeholdene
 * har sitt eget felt her framfor å ligge skjult i `notes`.
 */
export interface ExtendedOutlook {
  basis: "extended";
  daysOut: number;
  /** Hvor langt det finmaskede varselet rekker. */
  nearHorizonDays: number;
  /** Hvor langt denne kilden rekker. */
  horizonDays: number;
  forecastUntil: string;
  /** Dagen MET begynner å dekke avreisedagen — når anslaget blir et varsel. */
  refinesOn: string;
  /** Hvor mange av rutas dager kilden dekker, av hvor mange i alt. */
  covered: number;
  total: number;
  score: number;
  label: string;
  /** Modellen som svarte, navngitt slik den heter. */
  model: string;
  /** Bredden på modellens rutecelle, i kilometer. */
  gridKm: number;
  /** Lengste avstand fra et stopp til rutecellen som svarte for det. */
  farthestKm: number;
  /** Største høydekorreksjon lagt på temperaturene langs ruta. */
  correctionC: number;
  headline: string;
  notes: string[];
}

/** Klimaanslaget for hele ruta, når ingen modell rekker fram. */
export interface TripOutlook {
  basis: "normals";
  month: number;
  daysOut: number;
  horizonDays: number;
  /** Hvor langt den siste modellen rekker, før bare klima står igjen. */
  extendedHorizonDays: number;
  forecastUntil: string;
  covered: number;
  total: number;
  score: number;
  label: string;
  wetShare: number;
  snowShare: number;
  coldestC: number;
  coldestPlace: string;
  warmestC: number;
  headline: string;
  notes: string[];
}

/** Vinteren på ett stopp. Speiler api/src/types.ts — se data/winter.ts der. */
export type WinterActivityKind =
  | "aurora"
  | "ski-alpine"
  | "ski-nordic"
  | "ski-touring"
  | "dogsled"
  | "reindeer"
  | "snowshoe"
  | "whale"
  | "fishing"
  | "sauna"
  | "sledding"
  | "market"
  | "museum"
  | "ice-climbing"
  | "fjord-cruise"
  | "snowmobile"
  | "guided-hike"
  | "skating"
  | "cable-car"
  | "glacier";

export type WinterAccessStatus = "seasonal" | "harder" | "expert";

export interface WinterActivity {
  kind: WinterActivityKind;
  name: string;
  note: string;
  months: number[];
  website?: string;
  bookAheadDays?: number;
  fullDay?: true;
}

export interface WinterAccess {
  status: WinterAccessStatus;
  openMonths: number[];
  note: string;
  /** Om måneden ruta går i ligger innenfor sesongen stedet oppgir. */
  openThisMonth: boolean;
}

export interface Winter {
  month: number;
  access?: WinterAccess;
  activities: WinterActivity[];
}

export type ClarityStatus = "klar" | "delvis" | "tett";

/**
 * Om det er fri sikt fra et utsiktspunkt den dagen.
 *
 * `ViewScore` veier hele dagen — nedbør, dagslys, hvor utsiktsavhengig stedet
 * er. Denne svarer bare på om det står tåke der man skal opp, som er det
 * eneste spørsmålet foran bomstasjonen til Dalsnibba.
 */
export interface Clarity {
  score: number;
  status: ClarityStatus;
  cloudPercent: number;
  fogPercent: number;
}

/** Kjøretøyet turen kjøres med. Endrer hva planen advarer om, ikke hva den velger. */
export type VehicleKind = "bil" | "bobil" | "motorsykkel";

/** Over dette regnes kjøretøyet som stort. Skiltene i Norge går på meter. */
export const LARGE_VEHICLE_METERS = 6;

/** Hva veien fram til et stopp krever av kjøretøyet. */
export interface VehicleAccess {
  narrowRoad?: boolean;
  maxVehicleLengthMeters?: number;
  steepIncline?: boolean;
  note: string;
}

/** En lang tunnel på strekningen mellom to stopp. Bare de over fem km. */
export interface Tunnel {
  name: string;
  lengthKm: number;
  road: string;
  note: string;
}

export type LocalProducerKind = "sider" | "frukt" | "seter" | "gardsutsalg";

/** Selvbetjent bod eller seter i nærheten av et stopp. */
export interface LocalProducer {
  name: string;
  kind: LocalProducerKind;
  /** Ubemannet bod med tillitskasse — ta med kontanter eller Vipps. */
  selfService: boolean;
  season: string;
  note: string;
  website?: string;
}

export interface Daylight {
  sunrise: string | null;
  sunset: string | null;
  hours: number;
  midnightSun: boolean;
  polarNight: boolean;
}

/** En linje som stopper ved et stopp, og hvor ofte. Speiler api/src/types.ts. */
/** Et vindu med godt fotolys. Speiler api/src/types.ts. */
export interface GoldenWindow {
  from: string;
  to: string;
  minutes: number;
}

/**
 * Golden hour for et sted på en dato.
 *
 * `minutes` er selve opplysningen, ikke en detalj: vinduet er nesten tre timer
 * på Hardangervidda i juni og under førti minutter i Bergen i februar, så
 * «den siste timen før solnedgang» er feil begge veier.
 */
export interface GoldenHour {
  morning: GoldenWindow | null;
  evening: GoldenWindow | null;
  best: string | null;
  reason?: "polarNight" | "sunTooHigh";
  allDay?: boolean;
  midnightSun?: boolean;
}

/**
 * En holdeplass i nærheten av et stoppet. Speiler api/src/types.ts.
 *
 * Avstanden er *luftlinje*, ikke gangavstand — se doc-kommentaren der. Det er
 * også den lista er sortert på, så tallet man leser er tallet den er ordnet
 * etter.
 */
export interface NearbyStop {
  id: string;
  name: string;
  distanceKm: number;
  /** Transportmidlene som betjener den, sortert. Enturs rekkefølge er tilfeldig. */
  modes: string[];
}

/**
 * Hvorfor nabolista er tom, når den er tom. Speiler api/src/types.ts.
 *
 * `off-network` er stopp hvis koordinat ligger utenfor gangnettet — Svartisen,
 * Nigardsbreen, Hallingskarvet, Dovrefjell, Lysefjord. «Ingen holdeplass i
 * nærheten» er en påstand om stedet som ikke er sann for dem; vi vet bare ikke
 * veien dit.
 */
export type NearbyStatus = "ok" | "off-network" | "unavailable";

export interface StopLine {
  line: string;
  name?: string;
  mode: string;
  authority?: string;
  destination: string;
  departures: string[];
  perDay: number;
  atLeast?: boolean;
  everyMinutes?: number;
  realtime: boolean;
}

export interface StopImage {
  /** Sti under /stops/ — appen serverer bildene selv, se api/src/types.ts. */
  src: string;
  /** Stor utgave til rutepresentasjonen. Mangler der originalen er for liten. */
  wide?: string;
  wideWidth?: number;
  credit: string;
  license: string;
  sourceUrl: string;
}

/** Overnattingssted nær et stopp. Speiler api/src/types.ts. */
export interface Lodging {
  id: string;
  name: string;
  kind: "hotel" | "hostel" | "camping" | "cabin";
  lat: number;
  lon: number;
  distanceKm: number;
  dnt?: true;
  stars?: number;
  website?: string;
}

/** Et navngitt skianlegg. Speiler api/src/types.ts. */
export interface SkiArea {
  id: string;
  name: string;
  lat: number;
  lon: number;
  distanceKm: number;
  website?: string;
}

export interface SkiInfo {
  areas: SkiArea[];
  lifts: number;
  hasDownhill: boolean;
  hasNordic: boolean;
}

export interface Amenity {
  id: string;
  name: string;
  kind: "charging" | "fuel" | "food";
  lat: number;
  lon: number;
  distanceKm: number;
  brand?: string;
  capacity?: number;
  powerKw?: number;
  fastCharge?: boolean;
  diesel?: boolean;
  foodType?: "restaurant" | "cafe" | "fast_food";
  cuisine?: string;
}

/** Et stopp slik API-et sender det: tekstene er allerede oversatt. */
/** Hva slags opplevelse en attraksjon er. Navnet oversettes i i18n. */
export type OfferKind = "sight" | "viewpoint" | "hike" | "museum" | "food" | "activity";

/** Navngitt ting å se eller gjøre. Egennavnet står uoversatt med vilje. */
export interface PlannedOffer {
  kind: OfferKind;
  name: string;
  note?: string;
}

/** Hva den reisende er ute etter. Speiler api/src/types.ts. */
export type Interest =
  | "fjord"
  | "fjell"
  | "by"
  | "historie"
  | "mat"
  | "nordlys"
  | "midnattssol"
  | "strand"
  | "dyreliv"
  | "tog"
  | "ski";

/** En anbefalt reise, med begrunnelsen sin. */
export interface Recommendation {
  corridorId: string;
  name: string;
  tagline: string;
  score: number;
  reasons: string[];
  caution?: string;
}

/** Nordlysvarsel for ett sted én natt. Speiler api/src/types.ts. */
export interface AuroraOutlook {
  kp: number;
  requiredKp: number;
  chance: number;
  limitedBy: "daylight" | "clouds" | "activity" | null;
  /**
   * Hvor tallet kommer fra: «forecast» er NOAAs tretimersvarsel de tre neste
   * døgnene, «outlook» er 27-døgnsutsikten bygget på solrotasjonen — grovere,
   * men ekte. Skillet vises, så et anslag ikke leses som et varsel.
   */
  horizon: "forecast" | "outlook";
  /**
   * Andelen netter som normalt er klare denne måneden, 0–1.
   *
   * En normal, ikke et varsel — den går derfor ikke inn i `chance`. «I natt er
   * det overskyet, men 42 % av februarnettene er normalt klare her» er noe man
   * kan flytte en dag etter.
   */
  clearSkyNormal?: number;
}

/** Valutaene budsjettet kan vises i. Kursene kommer fra Norges Bank. */
export type CurrencyCode =
  | "NOK" | "EUR" | "USD" | "GBP" | "SEK" | "DKK"
  | "CHF" | "JPY" | "CNY" | "AUD" | "CAD" | "PLN";

/** Én linje i kostnadsanslaget. Speiler api/src/types.ts. */
export interface BudgetLine {
  key: "transport" | "tolls" | "ferry" | "lodging" | "food" | "activities";
  label: string;
  low: number;
  high: number;
  /**
   * Om linja deles av de som reiser sammen, eller påløper per hode.
   * Speiler api/src/types.ts. Kan mangle på en lagret plan fra før feltet fantes.
   */
  scope?: BudgetScope;
}

export type BudgetScope = "shared" | "personal";

/** Hva turen koster per person, ved et gitt antall som deler. Speiler api/src/types.ts. */
export interface SeatSplit {
  travellers: number;
  low: number;
  high: number;
}

export interface Budget {
  travellers: number;
  currency: "NOK";
  rates: Partial<Record<CurrencyCode, number>>;
  rateDate?: string;
  distanceKm: number;
  nights: number;
  lines: BudgetLine[];
  /** Drivstoff og strøm i mengde, ikke bare kroner. Speiler api/src/types.ts. */
  energy?: EnergyEstimate;
  /** Bomanleggene ruta går gjennom, med satsene fra NVDB. Bare for bil. */
  tolls?: TollEstimate;
  low: number;
  high: number;
  /**
   * Den delen av anslaget som deles, uansett hvor mange som blir med.
   *
   * Er den null — som på tog og buss, der alt er billetter — finnes det ingen
   * besparelse å love, og panelet viser da ingen kostnadsdeling.
   * Valgfri: en lagret plan fra før feltet fantes har den ikke.
   */
  shared?: { low: number; high: number };
  /** Hva turen koster per person ved én, to, tre og fire som deler. */
  perPerson?: SeatSplit[];
  disclaimer: string;
}

/**
 * Energien ruta koster, regnet fra kjørelengde og stigning.
 *
 * Speiler api/src/types.ts. Tusen høydemeter er rundt 1,8 liter eller 5,1
 * kilowattimer i seg selv, og det er derfor stigningen står som eget tall her.
 */
export interface EnergyEstimate {
  km: number;
  ascentM: number;
  descentM: number;
  litres: number;
  kWh: number;
  flatLitresPer100Km: number;
  flatKWhPer100Km: number;
  climbShareLitres: number;
  climbShareKWh: number;
}

/**
 * Bompengene, delt i det som er sikkert og det som avhenger av veivalget.
 *
 * `certain` er bomringene i byene ruta starter og ender i. `possible` er
 * veiprosjekter man bare betaler hvis man tar nettopp den veien — og appen har
 * ingen veidata. Speiler api/src/types.ts.
 */
export interface TollEstimate {
  certain: TollScheme[];
  possible: TollScheme[];
  sideRoads: TollScheme[];
  low: number;
  high: number;
  discount: number;
}

/** Ett bompengeanlegg fra NVDB. Speiler api/src/types.ts. */
export interface TollScheme {
  name: string;
  kind: "ring" | "project" | "private";
  stationCount: number;
  roads: string[];
  smallCar: number | null;
  rushSmallCar: number | null;
  largeCar: number | null;
  hourRuleMinutes: number | null;
  website: string | null;
}

/** Klimanormalen for én måned på ett sted. Speiler api/src/types.ts. */
export interface ClimateMonth {
  tMax: number;
  tMin: number;
  precipMm: number;
  wetDays: number;
  snowDays: number;
  frostNights: number;
  cloudPct: number;
  windKph: number;
}

/** Nøklene et plagg eller en gjenstand kan ha. Speiler api/src/services/messages.ts. */
export type PackingItem =
  | "woolBase"
  | "winterJacket"
  | "downJacket"
  | "midLayer"
  | "hatGloves"
  | "layers"
  | "rainShell"
  | "rainTrousers"
  | "windShell"
  | "sturdyBoots"
  | "iceGrips"
  | "shorts"
  | "tshirt"
  | "swimwear"
  | "sunscreen"
  | "sunglasses"
  | "sleepMask"
  | "insectRepellent"
  | "headlamp"
  | "firstAidKit"
  | "hikingPoles"
  | "powerBank"
  | "dryBag"
  | "binoculars"
  | "plugAdapter"
  | "reusableBottle";

/**
 * Ett plagg eller én gjenstand, klar til å vises. Speiler api/src/types.ts.
 *
 * `key` følger med den oversatte teksten fordi kategorien og søkelenka må slå
 * opp stabilt — å gjette kategori fra den viste teksten ville brutt for hvert
 * av de fire språkene som ikke er norsk eller engelsk.
 */
export interface PackedItem {
  key: PackingItem;
  label: string;
  category: "clothing" | "gear";
}

/**
 * Hva man trenger å ha med, utledet fra normalene langs ruta.
 *
 * Ett råd for hele ruta, ikke ett per stopp: man pakker én koffert, og den må
 * dekke det kaldeste stedet. Speiler api/src/types.ts.
 */
export interface PackingAdvice {
  month: number;
  coldestC: number;
  warmestC: number;
  coldestPlace: string;
  headline: string;
  items: PackedItem[];
  wetShare: number;
  snow: boolean;
}

/**
 * Et lyttetips langs ruta. Speiler api/src/types.ts.
 *
 * Lenkene er søk, ikke spillelister: appen oppretter ingenting på noens konto,
 * og en oppdiktet spilleliste-id ville sett ut til å virke uten å gjøre det.
 * `title` mangler der utvalget hviler på hvem artisten er framfor et album.
 */
export interface PlannedSoundtrack {
  kind: "music" | "podcast";
  artist: string;
  title?: string;
  note: string;
  spotify: string;
  appleMusic: string;
}

/** En navngitt badeplass nær et stopp. Speiler api/src/types.ts. */
export interface BathingSpot {
  name: string;
  kind: "beach" | "swim" | "resort";
  lat: number;
  lon: number;
  km: number;
}

/**
 * Sjøtemperaturen nå, fra MET sin havmodell. Speiler api/src/types.ts.
 *
 * `km` er hvor langt fra stoppet målepunktet ligger, og det avgjør hvor mye
 * tallet er verdt: null er stedet, seks kilometer er fjorden utenfor.
 */
export interface SeaTemperature {
  celsius: number;
  waveHeightM?: number;
  km: number;
  verdict: string;
  at: string;
}

/** En fredet bygning nær et stopp, fra Riksantikvaren. Speiler api/src/types.ts. */
export interface HeritageSite {
  name: string;
  kind: { no: string; en: string } | null;
  description: string | null;
  dated: string | null;
  datingMethod: string | null;
  protection: string | null;
  link: string | null;
  km: number;
}

/** Et veikamera fra Statens vegvesen. Speiler api/src/types.ts. */
export interface RoadCamera {
  id: string;
  name: string;
  /** Hvilken vei kameraet peker. «Fagernes» sier mer enn «kamera 2». */
  towards?: string;
  imageUrl: string;
  lat: number;
  lon: number;
  road?: string;
  elevationM?: number;
  distanceKm: number;
}

export type TrafficSeverity = "closed" | "convoy" | "warning" | "roadwork";

/** En aktiv trafikkmelding nær ruten. Speiler api/src/types.ts. */
export interface TrafficAlert {
  id: string;
  severity: TrafficSeverity;
  location: string;
  description: string;
  road?: string;
  lat: number;
  lon: number;
  nearestStopId: string;
  distanceKm: number;
  /** Dagen i planen meldingen rammer, der den lot seg plassere. */
  dayNumber?: number;
  /** Strekningen meldingen ligger på, når den ligger i veien man skal kjøre. */
  onLeg?: { fromStopId: string; fromName: string; toStopId: string; toName: string };
  from: string;
  until?: string;
  onMountainPass: boolean;
  inTunnel: boolean;
  url?: string;
}

/** Vernet natur nær et stopp. Speiler api/src/types.ts. */
export interface ProtectedArea {
  name: string;
  kind: string;
  authority?: string;
  factSheet?: string;
  /** Selve verneforskriften på lovdata.no. Det er den som gjelder. */
  regulation?: string;
}

export type HazardKind = "avalanche" | "flood" | "landslide";

/** Et aktivt naturfarevarsel fra NVE. Speiler api/src/types.ts. */
export interface HazardWarning {
  kind: HazardKind;
  /** 1–5 for snøskred, 1–4 for flom og jordskred. Bare 2 og over sendes. */
  level: number;
  area: string;
  headline: string;
  advice?: string;
  validFrom: string;
  validTo: string;
  stopIds: string[];
  url: string;
}

/** En ferjeavgang. Speiler api/src/types.ts. */
export interface FerryDeparture {
  fromQuay: string;
  toQuay: string;
  departure: string;
  arrival: string;
  durationMinutes: number;
  cancelled: boolean;
  operator?: string;
  operatorUrl?: string;
  notices: string[];
}

/** Ferjestrekningen ved et stopp, med de nærmeste avgangene. */
export interface FerryCrossing {
  stopId: string;
  quayName: string;
  toName: string;
  departures: FerryDeparture[];
  /** Dagen i planen overfarten faktisk skjer på. */
  dayNumber?: number;
}

export type HikeGrade = "enkel" | "moderat" | "krevende" | "ekspert";

/** Guidet tur med bestillingsfrist. Speiler api/src/types.ts. */
export interface GuidedTour {
  operator: string;
  website: string;
  /** Dager i forveien turen normalt bør bestilles — et råd, ikke en ledig plass. */
  bookAheadDays: number;
  note?: string;
}

/**
 * Fotturen som er siste etappe fram til stedet.
 *
 * Står dette, når ingen bil, buss eller ferje helt fram: `trailheadModes` sier
 * hva som når startpunktet, og resten går på egne bein.
 */
export interface Hike {
  hoursMin: number;
  hoursMax: number;
  distanceKm: number;
  ascentM: number;
  grade: HikeGrade;
  trailheadModes: Mode[];
  trailhead: string;
  season: string;
  timeWarning: string;
  guides: GuidedTour[];
}

export interface Stop {
  id: string;
  name: string;
  lat: number;
  lon: number;
  elevationM: number;
  tags: VibeTag[];
  blurb: string;
  /** 0 = korridorens startby, 1 = sluttby. */
  position: number;
  hoursFromStart: number;
  dwellHours: number;
  modes: Mode[];
  viewDependency: number;
  indoorAlternative?: string;
  enturStopId: string | null;
  winterWarning?: string;
  /** Siste etappe til fots, der ingen vei går fram. */
  hike?: Hike;
  /** Veikameraer i nærheten, med ferske bilder. */
  cameras?: RoadCamera[];
  /** Vernet natur i nærheten, med lenke til forskriften som gjelder der. */
  protectedAreas?: ProtectedArea[];
  image?: StopImage;
  /** Bakgrunn fra Wikipedia, på valgt språk, med lenke til artikkelen. */
  history?: string;
  historySource?: string;
  offers?: PlannedOffer[];
  amenities: Amenity[];
  lodging?: Lodging[];
  ski?: SkiInfo;
  /** Fredete bygninger i nærheten, med Riksantikvarens egen beskrivelse. */
  heritage: HeritageSite[];
  /** Navngitte badeplasser i nærheten. Tom liste er vanlig. */
  bathingSpots: BathingSpot[];
  /** Sjøtemperaturen nå — bare der stedet har sjø og datoen er i badesesongen. */
  sea?: SeaTemperature;
  /** Klimanormalen for reisemåneden på dette stedet. */
  climate?: ClimateMonth;
  /** Hva klimaet sier, der datoen ligger utenfor varselet. */
  outlook?: StopOutlook;
  weather?: Weather;
  viewScore?: ViewScore;
  /** Om det er fri sikt. Bare på utsiktspunkt og over 500 moh. */
  clarity?: Clarity;
  /** Hva veien fram hit krever av kjøretøyet, der noe er verdt å nevne. */
  vehicle?: VehicleAccess;
  /** Selvbetjente boder og setrer i nærheten. Tom liste er vanlig. */
  producers: LocalProducer[];
  /** Hva som er stengt og hva man gjør i stedet. Bare i vintermånedene. */
  winter?: Winter;
  aurora?: AuroraOutlook;
  /** Når lyset er godt for å fotografere stedet. */
  golden?: GoldenHour;
}

export interface Departure {
  line: string;
  mode: string;
  from: string;
  to: string;
  aimedDeparture: string;
  aimedArrival: string;
  durationMinutes: number;
}

/**
 * Én måte å komme seg mellom to stopp på.
 *
 * Entur svarer med flere forslag på samme spørsmål — buss hele veien, buss og
 * ferje, tog med bytte. Appen ba lenge om nøyaktig ett, og da så det ut som om
 * det bare fantes én forbindelse.
 */
export interface JourneyOption {
  /** Hele reisen dør til dør, inkludert ventetid mellom etappene. */
  durationMinutes: number;
  transfers: number;
  modes: string[];
  legs: Departure[];
}

/**
 * Én etappe mellom to nabostopp — hvordan man kommer seg fra det ene til det andre.
 *
 * `Departure` er én konkret avgang Entur kjenner. `Connection` er selve
 * strekningen, og finnes også der ingen rutegående transport går: den har
 * avstand, anslått tid og hva slags forflytning det er uansett.
 */
export interface Connection {
  fromStopId: string;
  fromName: string;
  toStopId: string;
  toName: string;
  /** Luftlinje i hele km. Veien er lengre — appen har ingen veidata. */
  straightKm: number;
  hours: number;
  mode: Mode;
  /** Kort nok til at det å gå er det riktige rådet. */
  walkable: boolean;
  /** Målet nås bare til fots, uansett hva man kom med. */
  trailhead: boolean;
  /**
   * At det også går tog eller buss på strekningen man kjører.
   *
   * Valgfritt av samme grunn som `journeys`: en lagret plan fra en eldre
   * versjon har ikke feltet. Se lib/offline.ts.
   */
  alsoTransit?: Mode;
  departures: Departure[];
  /**
   * Andre måter å reise strekningen på, raskeste først.
   *
   * Valgfritt her og påkrevd i API-et, og det er ikke en forglemmelse:
   * frontend leser også planer den ikke har laget selv — en lagret plan fra en
   * eldre versjon har ikke feltet. Se lib/offline.ts.
   */
  journeys?: JourneyOption[];
  /** Lange tunneler på strekningen. Bare de over fem km. */
  tunnels: Tunnel[];
  /** Høydeforskjell fra start til slutt, med fortegn. Grunnlaget for klimasjokkvarselet. */
  ascentM: number;
}

/**
 * Fotturen som eier dagen.
 *
 * Satt av inndelingen på serveren, ikke av visningen: er feltet satt, har
 * dagen ingen andre stopp — de er flyttet til nabodagene. Se
 * `reserveFullDayHikes` i api/src/services/itinerary.ts.
 */
export interface FullDayHike {
  stopId: string;
  name: string;
  hoursMin: number;
  hoursMax: number;
  trailhead: string;
  /** Om dagslyset rekker til turen den datoen. Undefined uten dato. */
  fitsDaylight?: boolean;
  daylightHours?: number;
}

export interface DayPlan {
  dayNumber: number;
  date: string | null;
  stops: Stop[];
  /**
   * Dagen som går med til å reise hjem.
   *
   * Har ingen stopp, og det er ikke en tom dag: uten dette flagget viser tavla
   * «fri dag» på den dagen brukeren tilbringer i bilen. Se `returnToStopId`.
   */
  homeward?: boolean;
  /** Fotturen som legger beslag på hele dagen, der dagen har en. */
  fullDayHike?: FullDayHike;
  legs: Departure[];
  /** Forflytningen mellom stoppene, én per mellomrom. Første går fra gårsdagens siste. */
  connections: Connection[];
  travelHours: number;
  distanceKm: number;
  notes: string[];
  daylight?: Daylight;
  /** Dagens fotolys, regnet for stoppet med mest å se på. */
  golden?: GoldenHour;
  viewScore?: number;
  viewLabel?: string;
}

export interface SwapExplanation {
  dayNumber: number;
  removed: string;
  added: string;
  gain: number;
  reason: string;
}

/**
 * Hvorfor én dag ser ut som den gjør, i tall fra optimalisereren.
 *
 * Merk hva som ikke er her: hvilket stopp som flyttet fra hvilken dag.
 * Algoritmen flytter dagsgrenser over en fast rekkefølge, så et enkeltstopp
 * kan ikke bytte dag alene — og grensesnittet skal ikke påstå at det gjorde det.
 */
export interface DayRationale {
  dayNumber: number;
  score: number;
  /** Plassering blant dagene etter score. 1 er den klareste. */
  clearestRank: number;
  /** Snitt viewDependency for stoppene på dagen, 0–1. */
  viewDependency: number;
  changed: boolean;
}

export interface Optimization {
  applied: boolean;
  baselineScore: number;
  optimizedScore: number;
  explanation: string[];
  swaps: SwapExplanation[];
  /** Tallene bak hver dag. Grensesnittet setter setningen; her står det som er regnet. */
  days?: DayRationale[];
}

export interface DepartureCandidate {
  date: string;
  score: number;
  label: string;
  delta: number;
  daylightHours: number;
}

export interface BestDeparture {
  best: DepartureCandidate;
  candidates: DepartureCandidate[];
  headline: string;
  inconclusive: boolean;
}

export interface CorridorSummary {
  id: string;
  name: string;
  tagline: string;
  region: string;
  startName: string;
  endName: string;
  modes: TravelMode[];
  /** Bildet som representerer ruta. Se heroStopId i api/src/data/corridors.ts. */
  image?: StopImage;
}

/** Korridor slik /api/corridors lister dem. */
/**
 * Når det er fullt av folk på en rute, og når det er tomt.
 *
 * Tallene er SSBs egne — ankomne gjester per reiselivsregion per måned, snittet
 * over sju hele år. Relative og ikke absolutte: 100 er rutas egen travleste
 * måned, ikke et antall mennesker. Spørsmålet er «når er det fullt *her*», og
 * det svaret er ulikt for Otta–Flåm og for Bodø–Tromsø.
 */
export interface CrowdSeason {
  /** Tolv tall, januar først, 0–100 av rutas egen topp. */
  months: number[];
  /** Hvor mange ganger travlere den travleste måneden er enn den roligste. */
  swing: number;
  peakMonth: number;
  quietestMonth: number;
  /** Regionen som svinger mest, med navn — et snitt skjuler nettopp den. */
  busiestRegion: { name: string; swing: number };
  /** Regionene tallet er satt sammen av, i rekkefølgen ruta går. */
  regions: string[];
}

export interface CorridorOption extends CorridorSummary {
  spanHours: number;
  stopCount: number;
  /** Utelatt bare om ruta ikke er tilordnet noen reiselivsregion. */
  crowd?: CrowdSeason;
}

export interface Itinerary {
  request: {
    corridorId: string;
    days: number;
    mode: TravelMode;
    /** Alltid satt i svaret; `bil` når forespørselen ikke oppga noe. */
    vehicle: VehicleKind;
    vibe: Vibe;
    pace: Pace;
    lang: Lang;
    startDate: string | null;
    optimizeForWeather: boolean;
    /**
     * Stedet planen handler om, når den handler om ett sted framfor om en rute.
     *
     * Valgfritt: en plan hentet fram fra lagringen kan være laget før feltet
     * fantes. Se lib/offline.ts.
     */
    focusStopId?: string;
    /** Stedet den reisende skal tilbake til. Se `returnToStopId`. */
    returnToStopId?: string;
    include: string[];
    exclude: string[];
  };
  corridor: CorridorSummary;
  days: DayPlan[];
  warnings: string[];
  totalStops: number;
  totalDistanceKm: number;
  totalTravelHours: number;
  highestPointM: number;
  optimization?: Optimization;
  bestDeparture?: BestDeparture;
  /** Aktive trafikkmeldinger nær ruten, alvorligste først. */
  trafficAlerts?: TrafficAlert[];
  /** Naturfarevarsler fra NVE langs ruten, alvorligste først. */
  hazards?: HazardWarning[];
  /** Ferjestrekninger ruta må over, med de nærmeste avgangene. */
  ferries?: FerryCrossing[];
  alternatives: Stop[];
  budget?: Budget;
  /** Klesrådet for måneden ruta går i. Krever at planen har en dato. */
  packing?: PackingAdvice;
  /**
   * Hva klimaet sier, når datoen ligger lenger fram enn noen modell rekker.
   * Settes bare da, og står i stedet for «ingen værdata for disse datoene».
   */
  outlook?: TripOutlook;
  /**
   * Langtidsvarselet, når MET ikke rekker fram men ECMWF gjør.
   *
   * Gjensidig utelukkende med `outlook`: det finnes ett svar på været, og to
   * paneler som begge svarer ville invitert til å tro på det snilleste.
   */
  extendedOutlook?: ExtendedOutlook;
  /** Noe å høre på. Redaksjonelt, med en faktisk tilknytning til ruta. */
  soundtrack: PlannedSoundtrack[];
  /**
   * Etappen fram til der ruta begynner, når man starter et annet sted.
   *
   * En `Connection`, så `ConnectionRow` tegner den uten at noe nytt må
   * skrives. Valgfri: en lagret plan fra en eldre versjon har den ikke, og de
   * fleste turer starter der ruta starter.
   */
  approach?: Connection;
  /** Etappen hjem, når den reisende har sagt hvor de skal tilbake til. */
  returnLeg?: Connection;
  /**
   * Stoppene i hver ende av reisen som ikke er dager i planen.
   *
   * Startstedet man reiser fra, og stedet man skal hjem til. Sendes med fordi
   * klienten ikke kan slå dem opp: `allStops` er stoppene på *ruta*, og Oslo
   * ligger ikke på Fjord-Norge. Valgfri — en lagret plan fra en eldre versjon
   * har feltet ikke. Se `endpoints` i api/src/types.ts.
   */
  endpoints?: Stop[];
}

/**
 * Hva en måned gir og hva den tar.
 *
 * Koder og ikke setninger. Teksten settes sammen her fra maler med tallplasser,
 * så ingen fakta bakes inn i en oversettelse — samme regel som `PackedItem.key`.
 */
export type MonthReason =
  | "midnightSun"
  | "aurora"
  | "everythingOpen"
  | "quiet"
  | "longDays"
  | "closures"
  | "shortDays"
  | "polarNight"
  | "busy"
  | "noAurora"
  | "noMidnightSun";

export interface MonthFacts {
  daylightSouth: number;
  daylightNorth: number;
  midnightSunNorth: boolean;
  polarNightNorth: boolean;
  closedStops: number;
  /** Navngitt, aldri bare talt: hvilke stopp avgjør om det angår turen din. */
  closedExamples: string[];
  auroraStops: number;
  /** Bare når en rute er valgt — tallet betyr ingenting uten en. */
  visitorLoad?: number;
  tMaxMedian: number;
  tMaxP25: number;
  tMaxP75: number;
}

export interface MonthAnswer {
  month: number;
  gives: MonthReason[];
  takes: MonthReason[];
  facts: MonthFacts;
  unmet: Interest[];
}

export interface MonthAdvice {
  /** Alle tolv, alltid. En måned blir aldri gjengitt som bare et nei. */
  months: MonthAnswer[];
  /**
   * Månedene som leverer alt det ble spurt om.
   *
   * En mengde, ikke en rangering. Er den tom, finnes det ingen måned som gir
   * alt — og da sier appen det framfor å kåre en vinner blant de nest beste.
   */
  matches: number[];
  /** Det som ble spurt om og som måneden ikke avgjør i det hele tatt. */
  noSeason: Interest[];
  corridorId?: string;
}

/** Et sted man kan si at man reiser fra. Endepunktene til rutene. */
export interface Origin {
  id: string;
  name: string;
  /** Til veibeskrivelsen fram dit. Se lib/deeplinks.ts. */
  lat: number;
  lon: number;
}

export interface TripForm {
  corridorId: string;
  days: number;
  mode: TravelMode;
  /** Kjøretøyet, for den som kjører selv. Styrer bare hva planen advarer om. */
  vehicle: VehicleKind;
  vibe: Vibe;
  pace: Pace;
  startDate: string;
  optimizeForWeather: boolean;
  /** Be om anbefaling av beste avreisedag i varselvinduet. */
  suggestBestDay: boolean;
  /**
   * Hvor den reisende starter, når det ikke er der ruta gjør det.
   *
   * Rutene begynner i Stavanger, Dombås, Otta — og appen antok at man alt
   * sto der. Tom streng betyr «jeg starter der ruta starter», som er
   * normaltilstanden. Se `Itinerary.approach`.
   */
  fromStopId: string;
  /**
   * Stedet turen skal handle om, framfor ruta den ligger på.
   *
   * Meldt fra bruk: «velger jeg bare ett stopp, hvorfor kan jeg ikke få en
   * rute bare for det? Jeg må ende opp med å planlegge hele ruta og fjerne de
   * andre stoppene, og det er veldig irriterende».
   *
   * Tom streng betyr «planlegg ruta», som er normaltilstanden. Er den satt,
   * står stedet i midten og dagene fylles av det som er innen dagsturavstand —
   * ingen påtvunget startby og sluttby. Se `focusStopId` i api/src/types.ts.
   */
  focusStopId: string;
  /**
   * Stedet den reisende skal tilbake til når turen er over.
   *
   * Meldt fra bruk: «hva med å komme tilbake? Om jeg må tilbake til Oslo og vil
   * ha en tur på fem dager, hvordan påvirker det planleggeren?»
   *
   * Tom streng betyr «jeg sier ikke noe om hjemreisen», som er
   * normaltilstanden. Er den satt, settes en dag av når reisen fyller en — og
   * planen sier hva det kostet. Se `returnToStopId` i api/src/types.ts.
   */
  returnToStopId: string;
  include: string[];
  exclude: string[];
  /**
   * Navnet på den som satte opp turen, til en delt lenke.
   *
   * Hele turen ligger alt i URL-en — se lib/url.ts — så en plan har vært
   * delbar hele tiden; ingenting sa det, og en lenke som kom inn var
   * anonym. Med et navn blir «Sushants tur» noe mottakeren kjenner igjen
   * framfor en adresse med tolv parametre.
   *
   * Tom streng når ingen har oppgitt noe, som er normaltilstanden. Frivillig
   * med vilje: navnet havner i en lenke som kan sendes videre, og det er ikke
   * appen som skal bestemme at man vil stå oppført på den.
   */
  ownerName: string;
}

/**
 * Turen til ett sted, framfor turen langs en rute.
 *
 * For den som så én reel og vil dit — ikke alle er glad i å ta hele veien.
 * Se api/src/services/placeTrip.ts.
 */
export interface NearbyPlace {
  stop: Stop;
  /** Reisetid én vei, i timer. */
  hours: number;
}

export interface PlaceTrip {
  stop: Stop;
  corridorId: string;
  /** Færreste døgn stedet i seg selv ber om. */
  minDays: number;
  days: { dayNumber: number; stops: Stop[] }[];
  nearby: NearbyPlace[];
  approach?: Connection;
  warnings: string[];
}

/**
 * Et sted slik mosaikken i «utforsk» trenger det.
 *
 * Lett med vilje: bakgrunnsteksten og attraksjonene er tunge og hentes først
 * når et sted faktisk åpnes. Se api/src/worker.ts, `/api/places`.
 */
export interface ExplorePlace {
  id: string;
  name: string;
  blurb: string;
  tags: string[];
  dwellHours: number;
  elevationM: number;
  hike: boolean;
  signature: boolean;
  image?: StopImage;
  corridors: { id: string; name: string }[];
}
